import { createSelector } from 'reselect'
import * as RenderItemTypes from 'constants/renderItemTypes'
import {
    FEATURE_WALLART,
    FEATURE_ERASER,
    FEATURE_STACKABLES,
    FEATURE_COMBINABLES,
    FEATURE_VERSIONS,
    LAST_SAVED_ON_WEB,
    FEATURE_WALLPAINT,
} from 'models/compositions/constants'

import { getSceneItemsById, getSelectedSceneId } from 'models/scenes/selectors'
import { getFurnitureById, getFurnitureIsLoading, getFurnitureIsLoadingByIds } from 'models/furniture/selectors'
import { getRouteCompositionIds } from 'models/location/selectors'
import { ROOM_SOURCE_USERCAPTURED, ROOM_SOURCE_STOCK } from 'models/tracking/constants'
import sortByArray from 'utils/sortByArray'

import { ART_FRAMES, ART_SIZES } from 'models/furniture/artConstants'
import convertCurrency from 'utils/convertCurrency'
import { buildTag } from 'utils/buildInfo'

const getCompositionsById = (state) => state.compositions.byId
const getSelectedId = (state) => state.compositions.selectedId
const getSelectedRenderItemIdRaw = (state) => state.compositions.selectedRenderItemId
const getSwappingRenderItemIdRaw = (state) => state.compositions.swappingRenderItemId
const getDraggingRenderItemIdRaw = (state) => state.compositions.draggingRenderItemId

export const getIsLoadingCompositions = (state) => state.compositions.isLoadingCompositions
export const getIsLoadingCompiledComposition = (state) => state.compositions.isLoadingCompiledComposition
export const getNextRenderItemId = (state) => state.compositions.nextRenderItemId
export const getNextStackableInfo = (state) => state.compositions.nextStackableItem
export const getSwappingSurfaces = (state) => state.compositions.swappingSurfaces
export const getWaitingSurfacesInteraction = (state) => state.compositions.nextStackableItem.waitingInteraction

export const getUserDidAddRenderItem = (state) => state.compositions.userDidAddRenderItem

export const getSurfacesInteractive = createSelector(
    [getSwappingSurfaces, getWaitingSurfacesInteraction],
    (isSwapping, isPlacing) => isSwapping || isPlacing,
)

export const getNextStackableFurnitureData = createSelector(
    [getNextStackableInfo, getFurnitureById],
    (nextStackableInfo, furnitureDataById) => furnitureDataById[nextStackableInfo.settings.id],
)

export const getHasCompositions = createSelector(
    [getCompositionsById],
    (compsById) => Object.keys(compsById).length > 1,
)

export const getSelectedCompositionId = createSelector(
    [getSelectedId, getRouteCompositionIds],
    (id, rIds) => rIds.compositionId || id,
)

export const getIsSaving = (state) => state.compositions.isSaving
export const getIsModified = (state) => state.compositions.isModified
export const getHasSaveError = (state) => state.compositions.hasSaveError

export const getSavedSettings = (state) => ({ ...state.compositions.savedSettings })

export const getSelectedCompositionData = createSelector(
    [getCompositionsById, getSelectedCompositionId],
    (compsById, selectedId) => compsById[selectedId] || null,
)

// Get the selectedRenderItemId
export const getSelectedRenderItemId = createSelector([getSelectedRenderItemIdRaw], (selectedId) => {
    if (selectedId > -1) {
        return selectedId
    }
    return null
})

export const getDraggingRenderItemId = createSelector([getDraggingRenderItemIdRaw], (draggingId) => {
    if (draggingId > -1) {
        return draggingId
    }
    return null
})

export const getSwappingRenderItemId = createSelector([getSwappingRenderItemIdRaw], (itemId) => {
    if (itemId > -1) {
        return itemId
    }
    return null
})

export const getIsDraggingRenderItem = createSelector([getDraggingRenderItemId], (draggingId) => !!draggingId)

export const getActiveSceneId = createSelector(
    [getSelectedCompositionData, getSelectedSceneId],
    (compositionItem, selectedSceneId) => {
        if (!compositionItem) {
            return null
        }

        // Default comp will get the selected scene
        const sceneId = compositionItem.sceneId || selectedSceneId

        if (!sceneId) {
            return null
        }
        return sceneId
    },
)

export const getSelectedScene = createSelector(
    [getSceneItemsById, getActiveSceneId],
    (sceneItemsById, selectedSceneId) => {
        if (!selectedSceneId) {
            return null
        }

        // Default comp will get the selected scene
        const scene = sceneItemsById[selectedSceneId]

        if (!scene) {
            // No scenes have been loaded
            return null
        }
        return scene
    },
)

export const getSelectedSceneRoomType = createSelector(
    [getSelectedScene],
    (selectedScene) => selectedScene && selectedScene.roomType,
)

export const getIsLoading = createSelector(
    [getFurnitureIsLoading, getFurnitureIsLoadingByIds],
    (furnitureIsLoading, furnitureIsLoadingByIds) => furnitureIsLoading || furnitureIsLoadingByIds,
)

// Used for the design page
export const getSelectedComposition = createSelector(
    [getSelectedCompositionData, getSelectedScene, getFurnitureById, getSelectedRenderItemId, getIsLoading],
    (compositionItem, selectedScene, productDataById, selectedRenderItemId, isLoading) => {
        if (!selectedScene) {
            return null
        }

        let hasFailedRenderItem = false

        // Furniture only (used for Pendo guide)
        const leftMostRenderItemId = Object.values(compositionItem.renderItems)
            .filter((i) => i.renderType === RenderItemTypes.FURNITURE_ITEM)
            .reduce((acc, item) => (item.x < acc[1] ? [item.uniqueId, item.x] : acc), [null, 99999])[0]

        let renderItems = Object.values(compositionItem.renderItems).map((renderItemPartial) => {
            const selected = renderItemPartial.uniqueId === selectedRenderItemId
            const isLeftMost = leftMostRenderItemId === renderItemPartial.uniqueId
            const productData = productDataById[renderItemPartial.id]
            const isCombinableChild = !!(
                renderItemPartial.combinablePlacement && renderItemPartial.combinablePlacement.parentId
            )

            if (!productData) {
                return null
            }
            switch (productData.renderType) {
                case RenderItemTypes.ART_ITEM: {
                    const { frameId, sizeId, colorId, matteId, customHeight } = renderItemPartial
                    const artItem = productData

                    const framePartial = ART_FRAMES[frameId]

                    let size = ART_SIZES[sizeId]
                    if (sizeId === 1) {
                        // The default size is the actual product size
                        size = {
                            ...size,
                            height: productData.displayHeightCm / 100,
                        }
                    } else if (sizeId === 999) {
                        size = {
                            ...size,
                            height: customHeight || 0.4,
                        }
                    }

                    // The colors are stored per-frame, but for now
                    // we're pretending like the color selection
                    // operates at the artItem scope (not the frame scope)
                    const color = framePartial.colors ? { ...framePartial.colors[colorId] } : null
                    const matte = framePartial.mattes ? { ...framePartial.mattes[matteId] } : null
                    const frame = {
                        ...framePartial,
                        color,
                        matte,
                    }

                    return {
                        ...renderItemPartial,
                        ...artItem,
                        frame,
                        size,
                        selected,
                        isLeftMost, // Always false for now (for furni only)
                    }
                }
                case RenderItemTypes.FURNITURE_ITEM: {
                    if (renderItemPartial.metrics && renderItemPartial.metrics.failed) {
                        hasFailedRenderItem = true
                    }

                    return {
                        ...renderItemPartial,
                        ...productData,
                        selected,
                        isLeftMost,
                        isCombinableChild,
                    }
                }
                default:
                    return null
            }
        })

        renderItems = renderItems.filter((item) => item !== null)

        const isMissingItems =
            (!isLoading && renderItems.length !== Object.keys(compositionItem.renderItems).length) ||
            hasFailedRenderItem

        const dynamicSurfaces = renderItems.flatMap((renderItem) =>
            (renderItem.stackables || []).map((stackable) => ({
                ...stackable,
                parent: renderItem,
                id: `${renderItem.uniqueId}${stackable.id}`,
            })),
        )

        const hiddenSurfaces = compositionItem.hiddenSurfaces

        const staticSurfaces = selectedScene.stackables.filter((surf) => !hiddenSurfaces.includes(surf.id))

        const stackableSurfaces = staticSurfaces.concat(dynamicSurfaces)

        const { manifest } = selectedScene

        const hasManifestEraseProps =
            manifest.pano_inpaint_0 &&
            manifest.depth_occlusion_inpaint_0 &&
            manifest.plane_orientation_inpaint_0 &&
            manifest.normals_segmentation_inpaint_0 &&
            (!manifest.segmentation_meta_0 || // Either we don't have the new prop or...
                // We do have the new prop so check the contents (support eraser for old scenes)
                (manifest.segmentation_meta_0.instances && manifest.segmentation_meta_0.instances.length))

        const canMagicErase = !!(hasManifestEraseProps && !selectedScene.isStock)

        return {
            ...compositionItem,
            sceneId: selectedScene.sceneId,
            scene: selectedScene,
            isMissingItems,
            renderItems,
            stackableSurfaces,
            canMagicErase,
        }
    },
)

export const getSelectedCompositionIsLoading = createSelector(
    [getSelectedComposition, getIsLoadingCompositions, getIsLoadingCompiledComposition],
    (compositionItem, isLoadingCompositions, isLoadingCompiledComposition) => {
        if (!compositionItem) {
            // Still loading compositions
            return isLoadingCompositions || isLoadingCompiledComposition
        }
        if (compositionItem.renderItems && compositionItem.renderItems.length) {
            return compositionItem.renderItems.some((ri) => !ri.readyAndRendered)
        }
        // Assume not loading by default
        return false
    },
)

// Get selected render item in the current composition
export const getSelectedRenderItem = createSelector(
    [getSelectedComposition, getSelectedRenderItemId],
    (compositionItem, renderItemId) => {
        if (!compositionItem || renderItemId < 0) {
            return null
        }
        const renderItem = compositionItem.renderItems.find((i) => i.uniqueId === renderItemId)

        return renderItem || null
    },
)

// Get composition data for saving server-side
export const getSelectedCompositionSaveData = createSelector(
    [getSelectedCompositionData, getActiveSceneId],
    (compositionData, selectedSceneId) => {
        // Tracks what features are used in the composition
        const features = {
            design: 1, // Informs mobile that we're using the features{} feature
        }

        const filteredRenderItems = Object.values(compositionData.renderItems).filter((item) => !item.hidden)
        const productItems = filteredRenderItems.map((item) => {
            if (item.frameId) {
                features[FEATURE_WALLART] = FEATURE_VERSIONS[FEATURE_WALLART]
            }
            if (item.stackedPlane) {
                features[FEATURE_STACKABLES] = FEATURE_VERSIONS[FEATURE_STACKABLES]
            }
            if (item.combinablePlacement) {
                features[FEATURE_COMBINABLES] = FEATURE_VERSIONS[FEATURE_COMBINABLES]
            }
            return {
                ...item,
                furnitureId: item.id,
                gfxAttributes: {
                    stackedPlane: item.stackedPlane,
                    combinablePlacement: item.combinablePlacement,
                    artData: {
                        sizeId: item.sizeId,
                        frameId: item.frameId,
                        colorId: item.colorId,
                        matteId: item.matteId,
                    },
                },
            }
        })

        const savedProperties = { ...compositionData.savedProperties }
        if (savedProperties.erasedSegments && savedProperties.erasedSegments.length) {
            features[FEATURE_ERASER] = FEATURE_VERSIONS[FEATURE_ERASER]
        }
        const hasWallPaintApplied = compositionData.wallPaint && Object.keys(compositionData.wallPaint).length > 0
        if (hasWallPaintApplied) {
            features[FEATURE_WALLPAINT] = FEATURE_VERSIONS[FEATURE_WALLPAINT]
            savedProperties.wallPaint = compositionData.wallPaint
        } else {
            delete savedProperties.wallPaint
        }

        return {
            compositionId: compositionData.id,
            placedFurniture: productItems,
            name: compositionData.name,
            sceneId: selectedSceneId,
            isShared: compositionData.isShared,
            savedProperties: {
                ...savedProperties,
                // Version data
                lastSavedOn: LAST_SAVED_ON_WEB,
                clientVersion: buildTag,
                clientLibVersions: buildTag,
                features,
            },
        }
    },
)

export const getCompositionsData = createSelector([getCompositionsById], (compData) =>
    Object.values(compData)
        .filter((item) => item.id !== -1)
        .sort((a, b) => b.id - a.id),
)

export const getUserCompositionsData = createSelector([getCompositionsData], (comps) => comps.filter((c) => !c.isStock))

export const getStockCompositionsData = createSelector([getCompositionsData], (comps) => comps.filter((c) => c.isStock))

export const getNonHiddenStockCompositionsData = createSelector([getCompositionsData], (comps) =>
    comps.filter((c) => {
        const { geoProperties, isStock } = c
        if (geoProperties && geoProperties.hidden) {
            return isStock && !geoProperties.hidden
        }
        return isStock
    }),
)

export const getStockCompositionsDataSorted = createSelector(
    [getStockCompositionsData, getSelectedCompositionId, getSelectedCompositionData],
    (comps, selectedCompId, selectedComposition) => {
        const getFirstWord = (title) => title.split(' ')[0] || ''
        const filteredComps = comps.filter((c) => c.id !== selectedCompId)
        const { name } = selectedComposition
        const orderByFirstWordSelectedCompName = getFirstWord(name).toUpperCase()

        return sortByArray(
            filteredComps,
            [(a) => getFirstWord(a.name), [orderByFirstWordSelectedCompName]],
            (a) => a.name,
        )
    },
)

export const getNumberCompositions = createSelector([getCompositionsData], (comps) => comps.length)

// Get the product (furniture and wall art) data of the rendered items in the composition
// and the uniqueId field of the renderItem
export const getSelectedCompositionProductData = createSelector(
    [getSelectedCompositionData, getFurnitureById],
    (selectedCompositionData, furnitureDataById) => {
        if (!selectedCompositionData || !selectedCompositionData.renderItems) {
            return []
        }
        return Object.values(selectedCompositionData.renderItems)
            .map((renderItem) => {
                const renderItemData = furnitureDataById[renderItem.id]
                if (!renderItemData) {
                    return null
                }
                return {
                    ...renderItemData,
                    uniqueId: renderItem.uniqueId,
                }
            })
            .filter((item) => !!item)
    },
)

// Filter out products that the user shouldn't be able to access in the UI
export const getSelectedCompositionShoppableProductData = createSelector(
    [getSelectedCompositionProductData],
    (allProductData) => allProductData.filter((item) => !item.hidden),
)

// Get the furniture data of the selected rendered item plus the uniqueId field
export const getSelectedCompositionRenderedFurnitureItem = createSelector(
    [getSelectedCompositionProductData, getSelectedRenderItemIdRaw],
    (renderItems, selectedRenderItemId) => {
        const renderItem = renderItems.find((i) => i.uniqueId === selectedRenderItemId)

        return renderItem || null
    },
)

// Get furnitureIds of missing furniture in the selected composition
export const getSelectedCompositionMissingFurnitureIds = createSelector(
    [getSelectedCompositionData, getFurnitureById],
    (selectedCompositionData, furnitureDataById) => {
        if (!selectedCompositionData || !selectedCompositionData.renderItems) {
            return []
        }
        return Object.values(selectedCompositionData.renderItems)
            .map((renderItem) => {
                const renderItemData = furnitureDataById[renderItem.id]
                if (!renderItemData) {
                    return renderItem.id
                }
                return null
            })
            .filter((i) => !!i)
    },
)

export const getSelectedCompositionTrackingRoomInfo = createSelector(
    [getSelectedScene, getSelectedCompositionData],
    (scene, composition) => {
        if (!scene) {
            return {}
        }
        const compData = {
            DesignId: null,
            DesignSource: null,
            DesignTitle: null,
        }
        if (composition) {
            if (composition.id !== -1) {
                compData.DesignId = composition.id
            }
            compData.DesignSource = composition.isStock ? 'Stock' : 'UserDesigned'
            if (composition.name) {
                compData.DesignTitle = composition.name
            }
        }
        return {
            ...compData,
            RoomTitle: scene.name,
            RoomId: scene.sceneId,
            RoomSource: scene.isStock ? ROOM_SOURCE_STOCK : ROOM_SOURCE_USERCAPTURED,
        }
    },
)

// Get the total price of all render items in the selected composition
export const getSelectedCompositionRenderedItemsTotalPrice = createSelector(
    [getSelectedCompositionShoppableProductData],
    (renderItems) => {
        // eslint-disable-next-line no-param-reassign
        const totalPrice = renderItems.reduce((total, renderItem) => (total += convertCurrency(renderItem.price)), 0)

        return `$${totalPrice.toFixed(2)}`
    },
)

// Get a unique set of render items ids for the selected composition
export const getSelectedCompositionRenderItemsLocalIds = createSelector(
    [getSelectedCompositionProductData],
    (renderItemsWithProductData) => [...new Set(renderItemsWithProductData.map((item) => item.localItemNumber))],
)
